import {
  useCallback,
  useRef,
  useState,
  memo,
  ChangeEvent,
  FormEvent,
  ReactNode,
} from 'react';

import {
  Box,
  Icon,
  Paper,
  Button,
  TextField,
  ClickAwayListener,
} from '@material-ui/core';

import { LightTooltip } from 'components';
import { SearchParams } from 'components/DataTable/types';
import { useDataTestId } from 'hooks';

import { useStyles } from './styles';

interface FloatSearchProps {
  onClose(): void;
  onSearch(text: string): void;
  onClean(): void;
  label?: string;
  columnId: string;
  searchParams: SearchParams<any>;
}

function FloatSearchWithoutMemo({
  onClose,
  onClean,
  onSearch,
  columnId,
  searchParams,
  label = 'Pesquisar',
}: FloatSearchProps): JSX.Element {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { getTestId } = useDataTestId();

  const [searchValue, setSearchValue] = useState<string>(() => {
    const oldValue = searchParams[columnId];

    return oldValue ?? '';
  });

  const handleSearch = useCallback(() => {
    if (searchValue) {
      onSearch(searchValue);
      onClose();
    }
  }, [onSearch, onClose, searchValue]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target as HTMLInputElement;

    setSearchValue(value);
  }, []);

  const handleClean = useCallback(() => {
    setSearchValue('');
    onClean();
  }, [onClean]);

  const renderButton = useCallback(
    (
      icon: string | ReactNode,
      ariaLabel: string,
      onClick: any,
      type = 'button',
    ) => {
      if (!searchValue) {
        return (
          <Button
            type={type}
            aria-label={ariaLabel}
            color="primary"
            size="small"
            className={classes.actionButton}
            onClick={onClick}
            disabled
          >
            {typeof icon !== 'string' ? icon : <Icon>{icon}</Icon>}
          </Button>
        );
      }

      return (
        <LightTooltip title={ariaLabel} placement="top">
          <Button
            type={type}
            aria-label={ariaLabel}
            color="primary"
            size="small"
            className={classes.actionButton}
            onClick={onClick}
          >
            <Icon>{icon}</Icon>
          </Button>
        </LightTooltip>
      );
    },
    [classes.actionButton, searchValue],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      handleSearch();
    },
    [handleSearch],
  );

  return (
    <Paper className={classes.paper} {...getTestId(`search-${columnId}`)}>
      <ClickAwayListener onClickAway={onClose}>
        <Box
          display="flex"
          alignItems="center"
          component="form"
          className={classes.actionContainer}
          onSubmit={handleSubmit}
        >
          <TextField
            autoFocus
            color="primary"
            variant="outlined"
            name="new-search"
            size="small"
            autoComplete="new-password"
            value={searchValue}
            onChange={handleChange}
            label={label}
            ref={inputRef}
            InputProps={{
              endAdornment: renderButton(
                <Icon color={searchValue ? 'primary' : 'inherit'}>close</Icon>,
                'Limpar pesquisa',
                handleClean,
              ),
            }}
          />
          {renderButton('arrow_forward', 'Pesquisar', handleSearch, 'submit')}
        </Box>
      </ClickAwayListener>
    </Paper>
  );
}

export const FloatSearch = memo(
  FloatSearchWithoutMemo,
) as typeof FloatSearchWithoutMemo;
